import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsDeliveryResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { Truck, CheckCircle2, Clock, XCircle, AlertCircle, MapPin, Store, Percent } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { CommonCardSkeleton, LineChartSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import CommonTable from '@/components/common/CommonTable';
import { formatChartDate } from '@/utility/helper';
import { ChartExportButton } from './ChartExportButton';

interface DeliverySectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsDeliveryResponse['data'] | null;
    isLoading: boolean;
}


export const DeliverySection: React.FC<DeliverySectionProps> = ({ filters, data, isLoading }) => {

    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const metrics = data?.metrics || {} as any;
    const charts = data?.charts || {} as any;
    const tables = data?.tables || {} as any;

    const kpiCards = [
        { label: 'Completed Deliveries', value: kpis.completedDeliveries ?? 0, icon: <Truck />, color: '#3B82F6' },
        { label: 'On-Time Deliveries', value: kpis.onTimeDeliveries ?? 0, icon: <CheckCircle2 />, color: '#10B981' },
        { label: 'Delayed Deliveries', value: kpis.delayedDeliveries ?? 0, icon: <Clock />, color: '#F59E0B' },
        { label: 'Failed Deliveries', value: kpis.failedDeliveries ?? 0, icon: <AlertCircle />, color: '#EF4444' },
    ];

    const metricCards = [
        { label: 'Average Pickup Time', value: `${metrics.avgPickupTime ?? 0}m`, icon: <Store />, color: '#8B5CF6' },
        { label: 'Average Delivery Time', value: `${metrics.avgDeliveryTime ?? 0}m`, icon: <Truck />, color: '#3B82F6' },
        { label: 'Avg Fulfillment Time', value: `${metrics.avgFulfillmentTime ?? 0}m`, icon: <Clock />, color: '#F59E0B' },
        { label: 'On-Time Percentage', value: `${metrics.onTimePercent ?? 0}%`, icon: <Percent />, color: '#10B981' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Delivery Performance</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=delivery"
                    fileName="delivery_performance.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {metricCards.map((card, i) => (
                    <CommonCard
                        key={i}
                        label={card.label}
                        value={card.value}
                        icon={card.icon}
                        iconColor={card.color}
                    />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Delivery Time Trend" icon={<Clock />}>
                    <div className="h-[300px] w-full">
                        {!charts.deliveryTimeTrend || charts.deliveryTimeTrend.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={charts.deliveryTimeTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value: number) => [`${value} mins`, 'Avg Time']}
                                    />
                                    <Area type="monotone" dataKey="avgTime" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="On-Time vs Delayed Deliveries" icon={<Truck />} >
                    <div className="h-[300px] w-full">
                        {!charts.onTimeVsDelayed || charts.onTimeVsDelayed.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={charts.onTimeVsDelayed}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        cursor={{ fill: '#F3F4F6' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar dataKey="onTime" name="On Time" stackId="a" fill="#10B981" barSize={32} />
                                    <Bar dataKey="delayed" name="Delayed" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={32} />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Stores with Fastest Delivery" className="overflow-x-auto">
                    <CommonTable
                        columns={[
                            { key: 'storeName', header: 'Store', className: 'font-medium text-foreground' },
                            { key: 'avgPrepTime', header: 'Avg Prep Time', render: (row: any) => `${row.avgPrepTime}m`, className: 'text-right', headerClassName: 'text-right' },
                            { key: 'avgDeliveryTime', header: 'Avg Delivery Time', render: (row: any) => `${row.avgDeliveryTime}m`, className: 'text-right font-medium text-success', headerClassName: 'text-right' }
                        ]}
                        data={tables.fastestStores || []}
                    />
                </SectionCard>

                <SectionCard title="Drivers with Fastest Delivery" className="overflow-x-auto">
                    <CommonTable
                        columns={[
                            { key: 'driverName', header: 'Driver', className: 'font-medium text-foreground' },
                            { key: 'deliveries', header: 'Deliveries', className: 'text-right', headerClassName: 'text-right' },
                            { key: 'avgDeliveryTime', header: 'Avg Delivery Time', render: (row: any) => `${row.avgDeliveryTime}m`, className: 'text-right font-medium text-success', headerClassName: 'text-right' }
                        ]}
                        data={tables.fastestDrivers || []}
                    />
                </SectionCard>
            </div>
        </div>
    );
};
